import { Box, type SxProps, type Theme } from "@mui/material";
import type { FC, ReactNode } from "react";
import { ICON_TILE } from "../../../theme/tokens";
import { AccentIconTile, type AccentIconTileSize } from "./AccentIconTile";

const MARK_BY_SIZE: Record<AccentIconTileSize, string> = {
	xs: ICON_TILE.XS.mark,
	sm: ICON_TILE.SM.mark,
	md: ICON_TILE.MD.mark,
	lg: ICON_TILE.LG.mark,
};

/** Longest letterform the tile can hold before the glyphs crowd its padding. */
const MAX_MARK_LENGTH = 3;

/**
 * Letterform for a brand with no explicit `mark`.
 *
 * Deliberately conservative — it covers the ordinary cases (an all-caps acronym, an intercapped
 * product name, a multi-word name) and nothing else. Names that are only distinguishable by a human
 * reading them (`Paycom` vs `Paycom SFTP` vs `Paycor`) collide under EVERY mechanical rule, which is
 * why `mark` exists: a catalog that has those pass their own letterforms rather than hoping a
 * derivation separates them.
 */
export const deriveBrandMark = (name: string): string => {
	const words = name.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
	if (words.length === 0) return "";
	if (words.length > 1)
		return words
			.map((word) => word[0])
			.join("")
			.slice(0, MAX_MARK_LENGTH);
	const [word] = words;
	// An acronym is already a mark — keep its letters rather than re-deriving one from them.
	if (word === word.toUpperCase()) return word.slice(0, MAX_MARK_LENGTH);
	// Intercaps name its own second letter (`iSolved` → `iS`, `ExcelForce` → `EF`); otherwise fall
	// back to the first two characters.
	const intercap = word.slice(1).search(/\p{Lu}/u);
	return intercap >= 0 ? `${word[0]}${word[1 + intercap]}` : word.slice(0, 2);
};

type Props = {
	/** The brand's display name — the derivation source for the letterform, and the logo's `title`. */
	name: string;
	/** Footprint, from the `ICON_TILE` token — `sm` (40px) is the list-row size. */
	size: AccentIconTileSize;
	/**
	 * Letterform to draw when there is no logo. Defaults to `deriveBrandMark(name)`; pass it
	 * explicitly wherever a catalog's names collide under a mechanical rule, or where the brand
	 * writes its own mark a particular way.
	 */
	mark?: string;
	/**
	 * Logo image URL. Renders INSTEAD of the letterform, contained inside the same tile — so a
	 * catalog can be given real vendor artwork later with no change at any call site. Ignored when
	 * `logo` is passed.
	 */
	logoSrc?: string;
	/** Fully-owned mark node (an inline SVG logo component). Wins over `logoSrc` and the letterform. */
	logo?: ReactNode;
	/** Letterform / logo-frame color. Defaults to the brand accent (`AccentIconTile`'s default). */
	color?: string;
	/** Tile background. Defaults to the brand-tint wash; pass a categorical tint to give each brand its own hue. */
	tint?: string;
	/** Root element — `"span"` when the tile sits inside phrasing content (a `<button>` row). */
	component?: "div" | "span";
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/**
 * A third-party brand's identity mark in the house tile: **its logo when we have one, its initials
 * when we don't.**
 *
 * The problem it solves is a vendor list that reads as 17 identical rows of text — an HRIS source
 * picker, an EMR list, an agency partner list. A mark per row gives each vendor a shape (and,
 * when the caller passes a categorical `tint`, a hue) so the list is scanned rather than read.
 *
 * Logo-ready by construction: `logoSrc`/`logo` render in place of the letterform inside the same
 * geometry, so dropping real artwork in later is a data change, not a UI change. We ship none today
 * — vendor logos are trademarked, need light/dark treatment, and their brand colors are off-token.
 *
 * Decorative by contract: the letterform is `aria-hidden` and a logo carries an empty `alt`, because
 * the tile always accompanies the brand's name in text. It never becomes the only announcement of
 * which vendor a row is — and it never makes a screen reader spell "AoD" before reading the name.
 */
export const BrandMarkTile: FC<Props> = ({ name, size, mark, logoSrc, logo, color, tint, component = "div", dataTestId, sx }) => {
	const letterform = mark ?? deriveBrandMark(name);
	return (
		<AccentIconTile size={size} color={color} tint={tint} component={component} dataTestId={dataTestId} sx={sx}>
			{logo ??
				(logoSrc ? (
					<Box
						component="img"
						src={logoSrc}
						alt=""
						// Contained, not cropped: vendor artwork is every aspect ratio there is, and a
						// squeezed logo reads as broken rather than as branding.
						sx={{ width: "72%", height: "72%", objectFit: "contain", display: "block" }}
					/>
				) : (
					<Box
						component="span"
						aria-hidden
						sx={{
							fontSize: MARK_BY_SIZE[size],
							fontWeight: 700,
							// Tightened: 2–3 capitals at default tracking drift apart inside a small square.
							letterSpacing: "-0.02em",
							lineHeight: 1,
							userSelect: "none",
						}}
					>
						{letterform}
					</Box>
				))}
		</AccentIconTile>
	);
};
